import React, { lazy, Suspense, useEffect } from 'react';

import { Navigate, Outlet, RouterProvider, createBrowserRouter, useNavigate } from 'react-router-dom';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useServerTime } from 'api/publicApi';
import MainLayout from 'components/MainLayout/MainLayout';
import SocketProvider from 'context/SocketContext';
import PrivateRoute from 'hoc/PrivateRoute';
import PublicRoute from 'hoc/PublicRoute';
import { useUserStore } from 'store/useUserStore';
import routes from 'utils/routes';

import {
  ROOT,
  HOME,
  GAME,
  HOW_TO_PLAY,
  HOW_TO_EARN,
  CONVERT,
  SELL_EARN,
  PURCHASE,
  FAQS,
  MYTOOLS,
  CRAFT,
  FORGOT_PASSWORD_SUCCESS,
  TERMS_OF_SERVICE,
  PRIVACY_POLICY,
} from './constants/routes';
import './App.css';

const Authentication = lazy(() => import('./pages/Auth/Authentication'));
const OtpVerification = lazy(() => import('./pages/Auth/Authentication/OtpVerification'));
const ForgotPassword = lazy(() => import('./pages/Auth/Authentication/ForgotPassword'));
const ForgotPasswordSuccess = lazy(() => import('./pages/Auth/Authentication/ForgotPasswordSuccess'));
const GamePage = lazy(() => import('./pages/GamePage'));
const Home = lazy(() => import('./pages/Home/Home'));
const HowToPlay = lazy(() => import('./pages/HowToPlay'));
const HowToEarn = lazy(() => import('./pages/HowToEarn'));
const Faqs = lazy(() => import('./pages/Faqs'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ExchangePage = lazy(() => import('./pages/Exchange/Exchange'));
const CraftPage = lazy(() => import('./pages/Craft/Craft'));
const Mytools = lazy(() => import('./pages/Mytools/Mytools'));

const errorElement = <div>404</div>;

const AppInit = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const handleEvent = (event) => {
      if (event.origin !== process.env.REACT_APP_ZOGOEX_URL) return;
      if (event.data.type === 'SEND_TOKEN') {
        const userData = JSON.parse(event.data.token);
        useUserStore.setState({
          ...userData?.state,
          isLoggedIn: true,
          isCrossAppLoginInProgress: false,
        });
        setTimeout(() => {
          navigate(event.data.location, {
            replace: true,
            state: event.data.metaData ?? null,
          });
        }, 0);
      }
    };

    window.addEventListener('message', handleEvent);

    return () => {
      window.removeEventListener('message', handleEvent);
    };
  }, []);

  useServerTime();
  useEffect(() => {
    if (!window.opener) return;
    window.opener?.postMessage({ type: 'REQUEST_TOKEN' }, process.env.REACT_APP_ZOGOEX_URL);
    useUserStore.setState({
      isCrossAppLoginInProgress: true,
    });
  }, []);

  return <Outlet />;
};

const router = createBrowserRouter([
  {
    path: ROOT,
    element: <AppInit />,
    errorElement: <div>404</div>,
    children: [
      {
        element: <PrivateRoute />,
        children: [
          {
            element: <MainLayout />,
            children: [
              { path: GAME, element: <GamePage />, errorElement, handle: { title: 'bottomNavBar.home' } },
              { path: CONVERT, element: <ExchangePage />, errorElement, handle: { title: 'bottomNavBar.exchange' } },
              { path: SELL_EARN, element: <ExchangePage />, errorElement, handle: { title: 'bottomNavBar.exchange' } },
              { path: PURCHASE, element: <ExchangePage />, errorElement, handle: { title: 'bottomNavBar.exchange' } },
              { path: CRAFT, element: <CraftPage />, errorElement, handle: { title: 'bottomNavBar.craftTools' } },
              { path: MYTOOLS, element: <Mytools />, errorElement, handle: { title: 'bottomNavBar.myTools' } },
            ],
          },
        ],
      },
      {
        element: <PublicRoute />,
        children: [
          { index: true, element: <Home />, errorElement },
          { path: FAQS, element: <Faqs />, errorElement },
          { path: TERMS_OF_SERVICE, element: <TermsOfService />, errorElement },
          { path: PRIVACY_POLICY, element: <PrivacyPolicy />, errorElement },
          { path: HOW_TO_PLAY, element: <HowToPlay />, errorElement },
          { path: HOW_TO_EARN, element: <HowToEarn />, errorElement },
          { path: routes.LOGIN, element: <Authentication />, errorElement },
          { path: routes.RESET_PASSWORD, element: <OtpVerification />, errorElement },
          { path: routes.SIGNUP, element: <Authentication />, errorElement },
          { path: routes.FORGOT, element: <ForgotPassword />, errorElement },
          { path: FORGOT_PASSWORD_SUCCESS, element: <ForgotPasswordSuccess />, errorElement },
          { path: routes.OTP_VERIFY, element: <OtpVerification />, errorElement },
          { path: routes.NOT_FOUND, element: <Navigate replace to={HOME} /> },
        ],
      },
    ],
  },
]);

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error?.response?.status === 503) {
        throw new Response('Under Maintenance', { status: 503 });
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      if (error?.response?.status === 503) {
        throw new Response('Under Maintenance', { status: 503 });
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false, // default: true
    },
  },
});

const App = () => {
  return (
    <Suspense fallback={<div />}>
      <QueryClientProvider client={queryClient}>
        <SocketProvider>
          <RouterProvider router={router} />
        </SocketProvider>
      </QueryClientProvider>
    </Suspense>
  );
};

export default App;
